// CF5/CF6 — collision-footprint policy for scenery. A pure mapping from (sprite, scale) to whether a
// decorative prop is big enough to read as a SOLID landmark, and how wide its ground footprint is.
// Big set-piece/landmark props (tall masts, the set-piece centrepiece, large boulders) are routed into
// world.obstacles at world-gen so they block consistently — no more walking through a boulder that sits
// right next to a solid twin. Small scatter / ambient / frame-ring markers stay pass-through scenery.
// Pure + deterministic: no THREE, no rng — just sprite + scale in, footprint out.

export interface Footprint {
  solid: boolean
  radius: number // collision radius in world units (0 when pass-through)
  blocksShots: boolean // chunky cover stops shots; thin masts let them whizz past
}

// thin, tall silhouettes — narrow ground base, shots pass beside them
const TALL_THIN = new Set(['prop:antenna', 'prop:flag', 'prop:satellite', 'prop:cactus', 'prop:deadtree'])
// chunky stone bodies — wide base, real cover
const CHUNKY = new Set(['prop:moonrock', 'prop:rock', 'prop:redrock', 'prop:crystal', 'prop:debris', 'prop:pillar', 'prop:grave'])
// smashable loot props (crate / pod / barrel) — roughly square bodies
const BREAKABLE = new Set(['break:crate', 'break:pod', 'break:barrel'])

// CF6 — collision radius as a fraction of the DRAWN billboard scale, per silhouette. Tall masts cover a
// narrow base; chunky stone fills most of its footprint; breakables are box-ish; unknown solids default
// chunky. Single source of truth for every obstacle/prop body so the hitbox matches what's rendered.
export const TALL_FACTOR = 0.18
export const CHUNKY_FACTOR = 0.34
export const BREAKABLE_FACTOR = 0.46

export function bodyRadius(sprite: string, scale: number): number {
  if (TALL_THIN.has(sprite)) return scale * TALL_FACTOR
  if (BREAKABLE.has(sprite)) return scale * BREAKABLE_FACTOR
  if (CHUNKY.has(sprite)) return scale * CHUNKY_FACTOR
  return scale * CHUNKY_FACTOR
}

// CF6 — an XP gem's grab radius tracks its drawn size (tier1 0.72 / tier2 1.0 / tier3 1.3) so you collect
// it when you visibly touch it (gems mostly magnetize in, so this is the final-touch radius).
export function gemRadius(tier: number): number {
  return tier >= 3 ? 0.6 : tier === 2 ? 0.46 : 0.35
}

// scale below which a piece reads as small decorative texture (always pass-through)
export const TALL_SOLID_MIN = 2.0
export const CHUNKY_SOLID_MIN = 2.4

const PASS: Footprint = { solid: false, radius: 0, blocksShots: false }

export function propFootprint(sprite: string, scale: number): Footprint {
  if (TALL_THIN.has(sprite)) {
    if (scale < TALL_SOLID_MIN) return PASS
    return { solid: true, radius: bodyRadius(sprite, scale), blocksShots: false }
  }
  if (CHUNKY.has(sprite)) {
    if (scale < CHUNKY_SOLID_MIN) return PASS
    return { solid: true, radius: bodyRadius(sprite, scale), blocksShots: true }
  }
  // craters (flat decals) and anything unlisted: pure scenery
  return PASS
}
